import { useMemo, useState } from 'react';
import { getServiceUrls } from '@order-up/common';

/**
 * Where "Back to menu" and "Order again" send a guest: the menu page they
 * actually came from.
 *
 * Two storefronts hand guests to checkout — the ordering app
 * (`ordering…/{slug}`) and the restaurant website (`www…/{slug}/menu`) — so
 * checkout cannot derive the page from the slug. The menu page passes its own
 * URL as `?menu=` (`getCheckoutLink`). Checkout's own `/{slug}` route is the
 * checkout page itself, so navigating there with no `?cart=` lands the guest
 * on "Cart Missing" (what "Order again" did).
 *
 * `menu` comes from the URL, so it is untrusted: followed as-is it would make
 * our "Back to menu" button a redirect to any site (CodeQL
 * js/client-side-unvalidated-url-redirection, and js/xss for a `javascript:`
 * value). So the returned URL is never the input string. Its ORIGIN is taken
 * from our own config — the input only selects WHICH storefront by an exact
 * origin match — and only the input's path (plus its `source` query
 * parameter) is kept, after that trusted `origin/` prefix. A value naming any other origin is ignored.
 *
 * A restaurant's custom domain is trusted only when it is THIS location's own
 * website domain, as the API resolved it from the verified domain registry
 * (`primaryDomain` on `GET /locations/slug/:slug`) — never because the URL or
 * `document.referrer` names it, which is exactly the open redirect. A guest who
 * ordered on houseofphoct.com goes back to houseofphoct.com; until the location
 * has loaded, or for any other host, the ordering app's page is the fallback.
 */

const STORAGE_PREFIX = 'checkout.menuUrl.';

const HOSTNAME = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/;

/**
 * The location's own website as trusted storefront bases: the domain and its
 * `www.` form (the site may be served on either). Empty for a missing or
 * malformed value.
 */
export function customDomainBases(primaryDomain: string | null | undefined): string[] {
    const host = (primaryDomain ?? '').trim().toLowerCase().replace(/^www\./, '');
    if (!HOSTNAME.test(host)) return [];
    return [`https://${host}`, `https://www.${host}`];
}

/** The ordering app's page for this location — the fallback, always ours. */
export function defaultMenuUrl(slug: string): string {
    return `${getServiceUrls().ordering.replace(/\/+$/, '')}/${encodeURIComponent(slug)}`;
}

/**
 * The menu URL to follow, rebuilt on one of `storefrontBases`, or null when
 * `candidate` does not name one of them.
 */
export function resolveMenuUrl(
    candidate: string | null,
    storefrontBases: readonly string[],
): string | null {
    if (!candidate) return null;
    let parsed: URL;
    try {
        parsed = new URL(candidate);
    } catch {
        return null;
    }
    for (const base of storefrontBases) {
        let trusted: URL;
        try {
            trusted = new URL(base);
        } catch {
            continue;
        }
        if (trusted.origin !== parsed.origin) continue;
        // Host from config; from the input only the path, behind `origin/`.
        const path = parsed.pathname.replace(/^\/+/, '');
        // The one query parameter that survives is `source` (where the guest
        // first arrived from), so the return trip does not lose it. Every
        // other parameter is dropped: a query can carry anything.
        const source = parsed.searchParams.get('source');
        const query = source ? `?${new URLSearchParams({ source }).toString()}` : '';
        return `${trusted.origin}/${path}${query}`;
    }
    return null;
}

function storageKey(slug: string): string {
    return `${STORAGE_PREFIX}${slug}`;
}

function remember(slug: string, url: string): void {
    try {
        window.sessionStorage.setItem(storageKey(slug), url);
    } catch {
        // Private mode / blocked storage: the confirmation page falls back to
        // the ordering app's page, which still works.
    }
}

function recall(slug: string): string | null {
    try {
        return window.sessionStorage.getItem(storageKey(slug));
    } catch {
        return null;
    }
}

/**
 * The menu URL for `slug`. The `?menu=` candidate is read once per page and
 * remembered for the tab, so the confirmation page (reached by an in-app
 * navigation that drops the query) still knows it. It is remembered as given
 * and rebuilt on every use, so it is followed only once it names one of our
 * storefronts or `primaryDomain` — which arrives with the location, so the
 * result updates when that loads.
 */
export function useMenuUrl(slug: string, primaryDomain?: string | null): string {
    const [candidate] = useState(() => {
        if (typeof window === 'undefined') return null;
        const fromParam = new URLSearchParams(window.location.search).get('menu');
        if (fromParam) {
            remember(slug, fromParam);
            return fromParam;
        }
        return recall(slug);
    });
    return useMemo(() => {
        if (typeof window === 'undefined') return defaultMenuUrl(slug);
        const services = getServiceUrls();
        const bases = [services.ordering, services.restaurant, ...customDomainBases(primaryDomain)];
        return resolveMenuUrl(candidate, bases) ?? defaultMenuUrl(slug);
    }, [candidate, slug, primaryDomain]);
}
